/**
 * Centralized style configuration for transcript rendering.
 *
 * Used by the rich HTML serializer (and future .docx export) to produce
 * inline styles that carry over when pasting into Word, Google Docs, etc.
 *
 * Property names use camelCase so they can be converted to kebab-case
 * CSS properties by the serializer.
 *
 * The per-line-type appearance (lineTypes below) is derived from the single
 * source of truth in `lineStyles.js`, so screen and export can never drift.
 * The serializers accept an optional config parameter, so customized styles
 * can be threaded in by building a config with overridden lineTypes.
 */

import {
  DEFAULT_LINE_STYLES,
  lineStyleToSerializerStyle,
} from './lineStyles';

/**
 * Build the `lineTypes` map (camelCase style objects keyed by lineType) from
 * a lineStyles object. Exported so callers can produce an export config that
 * reflects the user's customized styles.
 */
export function buildSerializerLineTypes(lineStyles = DEFAULT_LINE_STYLES) {
  return {
    default: {},
    speech: lineStyleToSerializerStyle(lineStyles.speech),
    embodied: lineStyleToSerializerStyle(lineStyles.embodied),
    gloss: lineStyleToSerializerStyle(lineStyles.gloss),
    translation: lineStyleToSerializerStyle(lineStyles.translation),
  };
}

const transcriptStyles = {
  // Applied to every <p> in serialized output
  base: {
    fontFamily: "'Courier New', Courier, monospace",
    fontSize: '10pt',
    lineHeight: '1.5',
    margin: '0',
  },

  // Paragraph-level styles by lineType attribute, derived from lineStyles.js
  lineTypes: buildSerializerLineTypes(DEFAULT_LINE_STYLES),

  // Inline mark styles
  marks: {
    bold: { fontWeight: 'bold' },
    italic: { fontStyle: 'italic' },
    underline: { textDecoration: 'underline' },
    strike: { textDecoration: 'line-through' },
    code: {
      fontFamily: "'Courier New', Courier, monospace",
      backgroundColor: '#f0f0f0',
      padding: '1px 3px',
      borderRadius: '2px',
    },
  },
};

/**
 * Build a complete serializer config (base + marks + line-type styles) that
 * reflects a user's customized line styles and active mode settings. Pass the result to
 * serializeToRichHTML / serializeToDOCX so clipboard and Word export match the
 * on-screen appearance.
 */
export function buildTranscriptStyles(
  lineStyles = DEFAULT_LINE_STYLES,
  { mode = 'multimodal', multilingual = false } = {}
) {
  const lineTypes = buildSerializerLineTypes(lineStyles);

  // Match the editor's presentation overrides without changing line data.
  if (mode === 'audible') {
    lineTypes.speech = {};
    lineTypes.embodied = {};
  }
  if (!multilingual) {
    lineTypes.gloss = {};
    lineTypes.translation = {};
  }

  return {
    ...transcriptStyles,
    lineTypes,
  };
}

export default transcriptStyles;
